import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as Mui from "@mui/material";
import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { Loader } from "components/Loader";
import { RequestInfoBanner } from "components/DesignSystem/RequestInfoBanner";
import { SecondaryButton } from "components/CustomComponents/DesignSystemButtons";
import { StatutoryPage } from "pages/PayRoll/Shared/statutoryUi";
import { NewRequestButton } from "pages/TeamZone/Approve/approveUi";
import { getSiteOptions } from "pages/Administration/Sites/sitesApi";
import { fetchPtOptions, fetchPtSites } from "./ptConfigApi";
import { PtSitesList } from "./PtSitesList";
import { PtSiteConfig } from "./PtSiteConfig";
import { usePayrollSettingsCrumbs } from "../../payrollSettingsMenu";
import { OpenDailyScreenButton } from "pages/PayRoll/Shared/settingsSwitch";

// Payroll > Configuration > Statutory > Professional Tax (screens PT1–
// PT1d): the sites list, and one site's PT configuration with its
// versions, slabs and exemptions, through the PT API (ptConfigApi.js). The
// open site is in the address (?item=pt&site=2) so Back and links from
// other screens (PT Validation's "Set up site") work.

export const PtConfiguration = () => {
  const { domain, topbarCompanyId } = useSelector((state) => state.tokenReducer);
  // Fresh data per company.
  return <PtConfigurationScreen key={`${domain}:${topbarCompanyId}`} domain={domain} companyId={topbarCompanyId} />;
};

const PtConfigurationScreen = ({ domain, companyId }) => {
  const settingsCrumbs = usePayrollSettingsCrumbs();
  const { search } = useLocation();
  const history = useHistory();
  const [list, setList] = useState({ loading: true, error: "", rows: [], cards: null });
  const [options, setOptions] = useState(null);
  // Active work locations (Administration › Sites). The PT list only has
  // sites with employees or already set up, so a new site is set up from here.
  const [workSites, setWorkSites] = useState([]);
  const [setUpRequest, setSetUpRequest] = useState(0);
  const controller = useRef(null);
  const siteId = Number(new URLSearchParams(search).get("site")) || null;

  const loadSites = useCallback(async () => {
    controller.current?.abort();
    if (!companyId) return;
    const request = new AbortController();
    controller.current = request;
    setList((current) => ({ ...current, loading: true, error: "" }));
    try {
      const data = await fetchPtSites({ domain, companyId, signal: request.signal });
      setList({ loading: false, error: "", rows: data.rows, cards: data.cards });
    } catch (err) {
      if (err.name === "AbortError") return;
      setList((current) => ({ ...current, loading: false, error: err.message || "Couldn't load the PT sites." }));
    }
  }, [domain, companyId]);

  useEffect(() => {
    loadSites();
    if (!companyId) return () => controller.current?.abort();
    const request = new AbortController();
    fetchPtOptions({ domain, companyId, signal: request.signal })
      .then(setOptions)
      .catch(() => {});
    getSiteOptions({ domain, signal: request.signal })
      .then((sites) => setWorkSites(Array.isArray(sites) ? sites : sites?.results || []))
      .catch(() => {});
    return () => {
      controller.current?.abort();
      request.abort();
    };
  }, [loadSites, domain, companyId]);

  // Sites PT doesn't list yet, as "not set up" rows for the Set up picker
  // and the site page title.
  const unlisted = useMemo(
    () =>
      workSites
        .filter((site) => site?.id != null && !list.rows.some((row) => String(row.id) === String(site.id)))
        .map((site) => ({ id: Number(site.id), site: site.name || "", code: site.code || "", state: site.state || "", set_up: false })),
    [workSites, list.rows],
  );
  const knownSites = useMemo(() => [...list.rows, ...unlisted], [list.rows, unlisted]);

  const openSite = (id) => history.push({ search: `?item=pt&site=${id}` });
  const backToList = () => {
    history.push({ search: "?item=pt" });
    loadSites();
  };

  if (siteId) {
    return (
      <PtSiteConfig
        key={siteId}
        domain={domain}
        companyId={companyId}
        siteId={siteId}
        siteRows={knownSites}
        options={options}
        onBack={backToList}
        onSitesChanged={loadSites}
      />
    );
  }

  return (
    <StatutoryPage
      breadcrumb={settingsCrumbs("Statutory", "Professional Tax")}
      title="Professional Tax"
      switchAction={<OpenDailyScreenButton group="statutory" item="pt" />}
      subtitle="PT is set up for each site. Click View to see or change a site's slabs and versions."
      aside={
        // Not set up yet: the page shows only the "Set up PT" banner (as ESI / LWF).
        list.error || (list.loading && !list.cards) || !list.rows.some((row) => row.set_up) ? null : (
          // Same header button as the Team Workspace Approve screens.
          <NewRequestButton
            hasAccess
            disabled={!list.rows.some((row) => !row.set_up) && !unlisted.length}
            onClick={() => setSetUpRequest((count) => count + 1)}
          >
            Set up site
          </NewRequestButton>
        )
      }
    >
      {list.error ? (
        <RequestInfoBanner
          tone="danger"
          title="Couldn't load the PT sites."
          body={list.error}
          action={
            <SecondaryButton onClick={loadSites} sx={{ whiteSpace: "nowrap" }}>
              Retry
            </SecondaryButton>
          }
        />
      ) : list.loading && !list.cards ? (
        <Mui.Box role="status" aria-label="Loading PT sites">
          <Loader info="Loading..." minHeight="40vh" />
        </Mui.Box>
      ) : (
        <PtSitesList rows={list.rows} cards={list.cards} unlisted={unlisted} onOpenSite={openSite} onSetUp={openSite} addRequest={setUpRequest} />
      )}
    </StatutoryPage>
  );
};
